import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo, faSave } from '@fortawesome/free-solid-svg-icons';
import { NgxMatDatepickerInput, NgxMatDatepickerToggle, NgxMatDatetimepicker } from '@ngxmc/datetime-picker';
import { BehaviorSubject, map } from 'rxjs';
import { SortInfos } from 'src/app/controls/advanced-gridview/models/sort-infos';
import { FormMode } from 'src/app/controls/common/form-mode';
import { Controls, TypedFormGroup } from 'src/app/controls/forms/managed-form/ngx-sub-form-utils';
import { FormGroupOptions } from 'src/app/controls/forms/managed-form/ngx-sub-form.types';
import { DataInput } from 'src/app/controls/forms/management-form/form.decorators';
import { ManagementFormComponent } from 'src/app/controls/forms/management-form/management-form';
import { RichToolTipDirective } from 'src/app/directives/rich-tool-tip.directive';
import { Attribute } from 'src/app/models/attribute';
import { AttributeValue } from 'src/app/models/attribute-value';
import { PropertyType } from 'src/app/models/enums/property-type';
import { FolderState } from 'src/app/models/folder-state';
import { IAttribute } from 'src/app/models/interfaces/attribute.interface';
import { RestApiConfiguration } from 'src/app/models/RestApiCaller/configuration/rest-api-configuration';
import { ContextOfUse } from 'src/app/models/RestApiCaller/Enums/context-of-use';
import { User } from 'src/app/models/user';
import { FoldersStatesService } from 'src/app/services/folders-states.service';
import { RestApiConfigurationsService } from 'src/app/services/restApi/rest-api-configurations.service';
import { UsersService } from 'src/app/services/users.service';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { AttributeFormContext } from '../attribute-form-context';
import { AttributeOptionsDialogComponent } from './attribute-options-dialog/attribute-options-dialog';
import { AttributeOptionsDialogResult } from './attribute-options-dialog/models/attribute-options-dialog-result';

@Component({
    selector: 'app-attribute-form',
    templateUrl: './attribute-form.component.html',
    styleUrls: ['./attribute-form.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        FaIconComponent,
        MatTooltipModule,
        MatSelectModule,
        MatOptionModule,
        MatDatepickerModule,
        NgClass,
        MatCheckboxModule,
        MatButtonModule,
        RichToolTipDirective,
        NgxMatDatepickerInput,
        NgxMatDatetimepicker,
        NgxMatDatepickerToggle,
    ],
})
export class AttributeFormComponent extends ManagementFormComponent<Attribute, IAttribute> implements OnInit, OnChanges {
    /**
     * Liste des icônes utilisées par le template
     */
    public faSave = faSave;
    public faCircleInfo = faCircleInfo;

    /**
     * Liste des Enums utilisées par le template
     */
    public FormMode = FormMode;
    public propertyType: typeof PropertyType = PropertyType;

    /**
     * Données de l'attribut à afficher dans le formulaire.
     */
    @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('attribute')
    public dataInput: Required<Attribute> | null | undefined;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: AttributeFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<AttributeFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: AttributeFormContext) {
        this._context$.next(value);
    }

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Valeurs d'un attribut de type liste.
     */
    public attributeListValues: AttributeValue[] = null;

    /**
     * Valeurs possibles de l'attribut 'Etat de dossier'.
     */
    public folderStates: FolderState[] = null;

    /**
     * Valeurs possibles de l'attribut 'Utilisateurs'.
     */
    public userList: User[] = null;

    /**
     * Liste des configurations d'API.
     */
    public _apiConfigurations: RestApiConfiguration[];

    /**
     * Type d'attribut sélectionné.
     */
    public selectedAttributeType: PropertyType = null;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('attributeUpdated')
    public dataOutput: EventEmitter<Attribute> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annulé (clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    /**
     * Constructeur.
     * @param foldersStatesService : Service gérant les états de dossier.
     * @param apiConfigurationsService : Service gérant les configurations d'API.
     */
    constructor(
        private foldersStatesService: FoldersStatesService,
        private userService: UsersService,
        private apiConfigurationsService: RestApiConfigurationsService,
        public dialog: MatDialog
    ) {
        super();
    }

    /**
     * Appellée à l'initialisation du composant.
     */
    override ngOnInit() {
        super.ngOnInit();
        console.log('Attribute Form - ngOnInit');
        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.item);

            this.selectedAttributeType = x.item.type;

            if (x.item.type === PropertyType.List || x.item.type === PropertyType.ListCustomers) {
                let attribute = this._context.item as Attribute;

                if (attribute.name === 'FolderStateId') {
                    this.foldersStatesService
                        .List(null, null, null, null)
                        .pipe(
                            map((x) => {
                                x.value.sort((a, b) => {
                                    return a.order < b.order ? -1 : 1;
                                });
                                return x.value;
                            })
                        )
                        .subscribe((states) => {
                            this.folderStates = states;
                        });
                } else {
                    this.attributeListValues = attribute.attributeValueDefinitions;
                }
            }

            if (x.item.type === PropertyType.AutocompletionByApi) {
                this.loadRestApiConfigurations();
            }

            if (x.item.type === PropertyType.ListUsers) {
                this.userService.List(null, null, null, new SortInfos('displayName')).subscribe((users) => {
                    this.userList = users.value;
                });
            }

            this.formGroup.get('id').disable();

            // L'ordre est non modifiable via le formulaire, la mise à jour se fait uniquement via la liste
            this.formGroup.get('order').disable();

            // La propriété indiquant si l'attribut est personnalisé ne peut pas être modifié
            this.formGroup.get('custom').disable();

            // Rend le champ name read-only en édition, il ne peut être modifié qu'à la création
            if (this._context?.mode === FormMode.Edition) {
                this.formGroup.get('name').disable();
            }

            // // La propriété indiquant si l'attribut est personnalisé ne peut pas être modifié
            // this.formGroup.get('custom').disable();

            // A la création la propriété indiquant si l'attribut est personnalisé est systématiquement à true
            if (this._context?.mode === FormMode.Creation) {
                this.formGroup.get('custom').patchValue(true);
            }

            // A la création la propriété indiquant si l'attribut est 'Non visible dans l'interface utilisateur' est systématiquement à false
            if (this._context?.mode === FormMode.Creation) {
                this.formGroup.get('invisible').patchValue(false);
            }

            if (!!this._context) {
                if (this._context?.mode !== FormMode.Reading) {
                    if (!this._context?.item.custom) {
                        this.enableCustomizableFieldsForSystemAttribute();
                    } else {
                        this.enableAllFieldsForCustomAttribute();
                    }
                }
            }

            this.checkIfFormatFieldIsEnabled();
            this.checkIfDisplayFormatFieldIsEnabled();
            this.checkIfValidatorsNeededForDefaultValueField();
            this.checkIfValidatorsNeededForconfigurationApiIdField();
        });
    }

    /**
     * Appellée lorsque les données bindées du composant change (input)
     */
    ngOnChanges() {
        console.log('Attribute Form - ngOnChanges');
        // this.formGroup.get('id').disable();

        // // L'ordre est non modifiable via le formulaire, la mise à jour se fait uniquement via la liste
        // this.formGroup.get('order').disable();

        // // Rend le champ name read-only en édition, il ne peut être modifié qu'à la création
        // if (this._context?.mode === FormMode.Edition) {
        //     this.formGroup.get('name').disable();
        // }

        // // La propriété indiquant si l'attribut est personnalisé ne peut pas être modifié
        // this.formGroup.get('custom').disable();

        // if (!!this._context) {
        //     if (this._context?.mode !== FormMode.Reading) {
        //         if (!this._context?.item.custom) {
        //             this.enableCustomizableFieldsForSystemAttribute();
        //         } else {
        //             this.enableAllFieldsForCustomAttribute();
        //         }
        //     }
        // }

        // Désactive les options 'Favoris' et 'Afficher à la création' si l'option 'Non visible dans l'interface utilisateur' est activée
        if (this.formGroup.get('invisible').value === true) {
            this.formGroup.get('isFavorite').disable();
            this.formGroup.get('displayedInCreationForm').disable();
        }
    }

    /**
     * Appelée lorsque l'utilisateur change le type de l'attribut.
     */
    public onSelectionTypeChange() {
        this.selectedAttributeType = this.formGroup.get('type').value;

        if (this.selectedAttributeType === PropertyType.AutocompletionByApi) {
            this.loadRestApiConfigurations();
        }

        if (!!this._context) {
            if (this._context?.mode !== FormMode.Reading) {
                if (!this._context?.item.custom) {
                    this.enableCustomizableFieldsForSystemAttribute();
                } else {
                    this.enableAllFieldsForCustomAttribute();
                }
            }
        }

        this.checkIfFormatFieldIsEnabled();
        this.checkIfDisplayFormatFieldIsEnabled();
        this.checkIfValidatorsNeededForDefaultValueField();
        this.checkIfValidatorsNeededForconfigurationApiIdField();

        this.resetDefaultValueField();
        this.resetFormatField();
        this.resetDisplayFormatField();
        this.resetFieldValidators();
    }

    /**
     * Appelée lorsque l'utilisateur coche ou decoche l'option 'Visible'.
     * @param event : Evenement.
     */
    public onInvisibleOptionChange(event) {
        if (this.formGroup.get('invisible').value === true) {
            this.formGroup.get('isFavorite').patchValue(false);
            this.formGroup.get('displayedInCreationForm').patchValue(false);
            this.formGroup.get('isFavorite').disable();
            this.formGroup.get('displayedInCreationForm').disable();
        } else {
            this.formGroup.get('isFavorite').enable();
            this.formGroup.get('displayedInCreationForm').enable();
        }
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        // Force le nom car il n'est pas fourni à l'API car le champ est disabled
        this.dataValue.name = this.formGroup.get('name').value;

        // Force le format dans le cas d'un attribut de type email/n° de téléphone car le champ est disabled
        if (this.formGroup.get('type').value === PropertyType.Email || this.formGroup.get('type').value === PropertyType.PhoneNumber) {
            this.dataValue.format = this.formGroup.get('format').value;
        }

        if (this._context?.item.custom === false) {
            this.dataValue.type = this.formGroup.get('type').value;
        }

        // Dans le cas d'une valeur par défaut pour un attribut de type 'PhoneNumber', stocke la valeur au format XXXXXXXXXX.
        if (this.dataValue.type === PropertyType.PhoneNumber) {
            if (this.dataValue.defaultValue) {
                this.dataValue.defaultValue = this.dataValue.defaultValue.replace(/[ .-]+/g, '');
            }
        }

        super.manualSave();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public cancel(): void {
        console.log('validité', this.formGroup);
        //this.canceled.emit(true);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Options...'.
     */
    public displayOptions(): void {
        console.log('displayOptions', this.formGroup);

        const dialogRef = this.dialog.open(AttributeOptionsDialogComponent, {
            maxWidth: '400px',
            data: {
                item: this._context.item.options,
                readonly: this._context.mode === FormMode.Reading,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: AttributeOptionsDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                console.log('update options', dialogResult.item);
                this.dataValue.options = dialogResult.item;
            }
        });
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<IAttribute> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            organizationId: new UntypedFormControl(null),
            name: new UntypedFormControl(null, Validators.required),
            type: new UntypedFormControl(null, Validators.required),
            configurationApiId: new UntypedFormControl(null),
            format: new UntypedFormControl(null),
            displayFormat: new UntypedFormControl(null, Validators.required),
            defaultValue: new UntypedFormControl(null),
            order: new UntypedFormControl(null),
            enable: new UntypedFormControl(null, Validators.required),
            custom: new UntypedFormControl(null, Validators.required),
            mandatory: new UntypedFormControl(null, Validators.required),
            isFavorite: new UntypedFormControl(null, Validators.required),
            displayedInCreationForm: new UntypedFormControl(null, Validators.required),
            header: new UntypedFormControl(null, Validators.required),
            usedBySearch: new UntypedFormControl(null, Validators.required),
            invisible: new UntypedFormControl(null, Validators.required),
        };
    }

    /**
     * Retourne les options de validation à fournir au niveau du groupe de formulaire.
     * @returns les options de validation.
     */
    protected override getFormGroupControlOptions(): FormGroupOptions<IAttribute> {
        return {
            validators: [this.onlyWordAndDigit('name')],
        };
    }

    /**
     * Charge les configurations d'API.
     */
    private loadRestApiConfigurations() {
        this.apiConfigurationsService.List().subscribe((x) => {
            let configurations = x.value.sort((a, b) => {
                return a.name < b.name ? -1 : 1;
            });
            this._apiConfigurations = configurations.filter((x) => x.contextOfUse === ContextOfUse.AutocompletionDatasource);
        });
    }

    /**
     * Active les champs qui sont personnalisable dans le cas d'un attribut système.
     * Ne pas appeler pour un attribut personnalisé.
     */
    private enableCustomizableFieldsForSystemAttribute(): void {
        // Les champs suivants ne sont pas personnalisables
        this.formGroup.get('type').disable();
        this.formGroup.get('format').disable();
        this.formGroup.get('enable').disable();

        // Les champs suivants sont personnalisables
        this.formGroup.get('header').enable();
        this.formGroup.get('displayFormat').enable();
        this.formGroup.get('defaultValue').enable();
        this.formGroup.get('mandatory').enable();
        this.formGroup.get('isFavorite').enable();
        this.formGroup.get('displayedInCreationForm').enable();
        this.formGroup.get('usedBySearch').enable();
        this.formGroup.get('invisible').enable();
    }

    /**
     * Active tous les champs dans le cas d'un attribut personnalisés.
     * Ne pas appeler pour un attribut système.
     */
    private enableAllFieldsForCustomAttribute(): void {
        this.formGroup.get('header').enable();
        this.formGroup.get('type').enable();
        this.formGroup.get('configurationApiId').enable();
        this.formGroup.get('format').enable();
        this.formGroup.get('displayFormat').enable();
        this.formGroup.get('defaultValue').enable();
        this.formGroup.get('enable').enable();
        this.formGroup.get('mandatory').enable();
        this.formGroup.get('isFavorite').enable();
        this.formGroup.get('displayedInCreationForm').enable();
        this.formGroup.get('usedBySearch').enable();
        this.formGroup.get('invisible').enable();
    }

    /**
     * Vérifie si le type d'attribut sélectionné nécessite que le champ 'Format de saisie' soit activé.
     */
    private checkIfFormatFieldIsEnabled() {
        if (
            (this.selectedAttributeType === PropertyType.Text || this.selectedAttributeType === PropertyType.Decimal || this.selectedAttributeType === PropertyType.Long) ===
                false ||
            this._context.mode === FormMode.Reading
        ) {
            this.formGroup.get('format').disable();
        }
    }

    /**
     * Vérifie si le type d'attribut sélectionné nécessite que le champ 'Format d'affichage' soit activé.
     */
    private checkIfDisplayFormatFieldIsEnabled() {
        if (
            (this.selectedAttributeType === PropertyType.Date ||
                this.selectedAttributeType === PropertyType.DateAndTime ||
                this.selectedAttributeType === PropertyType.PhoneNumber) === false ||
            this._context.mode === FormMode.Reading
        ) {
            this.formGroup.get('displayFormat').disable();
        }
    }

    /**
     * Vérifie si le type d'attribut sélectionné nécessite la mise en place de validateurs sur le champ 'Valeur par défaut'.
     */
    private checkIfValidatorsNeededForDefaultValueField() {
        if (this.selectedAttributeType === PropertyType.Email) {
            this.formGroup.get('defaultValue').setValidators([Validators.pattern(this.emailPattern)]);
        } else if (this.selectedAttributeType === PropertyType.PhoneNumber) {
            this.formGroup.get('defaultValue').setValidators([Validators.pattern(this.phonePattern)]);
        }
    }

    /**
     * Verifie si le type d'attribut selectionné necessite la mise en place de validateurs sur le champ 'API fournissant la source de données d'API'.
     */
    private checkIfValidatorsNeededForconfigurationApiIdField() {
        if (this.selectedAttributeType === PropertyType.AutocompletionByApi) {
            this.formGroup.get('configurationApiId').setValidators([Validators.required]);
        }
    }

    /**
     * Réinitialise la valeur du champ 'Valeur par défaut'.
     */
    private resetDefaultValueField() {
        if (this.formGroup.get('type').value === PropertyType.Color) {
            this.formGroup.get('defaultValue').setValue('');
        } else {
            this.formGroup.get('defaultValue').patchValue('');
        }
    }

    /**
     * Réinitialise la valeur du champ 'Format de saisie'.
     */
    private resetFormatField() {
        if (this.formGroup.get('type').value === PropertyType.Email) {
            this.formGroup.get('format').setValue(this.emailPattern);
        } else if (this.formGroup.get('type').value === PropertyType.PhoneNumber) {
            this.formGroup.get('format').setValue(this.phonePattern);
        } else {
            this.formGroup.get('format').patchValue('');
        }
    }

    /**
     * Réinitialise la valeur du champ 'Format d'affichage'.
     */
    private resetDisplayFormatField() {
        if (this.formGroup.get('type').value === PropertyType.Date) {
            this.formGroup.get('displayFormat').patchValue(1);
        } else if (this.formGroup.get('type').value === PropertyType.DateAndTime) {
            this.formGroup.get('displayFormat').patchValue(3);
        } else if (this.formGroup.get('type').value === PropertyType.PhoneNumber) {
            this.formGroup.get('displayFormat').patchValue(8);
        } else {
            this.formGroup.get('displayFormat').patchValue('');
        }
    }

    /**
     * Réinitialise les validateurs des champs.
     */
    private resetFieldValidators() {
        if (this.formGroup.get('type').value === PropertyType.Email) {
            this.formGroup.get('defaultValue').setValidators([Validators.pattern(this.emailPattern)]);
        } else if (this.formGroup.get('type').value === PropertyType.PhoneNumber) {
            this.formGroup.get('defaultValue').setValidators([Validators.pattern(this.phonePattern)]);
        } else {
            this.formGroup.get('defaultValue').setValidators([]);
        }

        if (this.formGroup.get('type').value === PropertyType.AutocompletionByApi) {
            this.formGroup.get('configurationApiId').setValidators([Validators.required]);
        } else {
            this.formGroup.get('configurationApiId').setValidators([]);
        }
    }

    /**
     * Fonction permettant de valider qu'un contrôle ne contient que des lettres et des chiffres.
     * @param controlName : Nom du contrôle à verifier.
     * @returns : Une rreur de validation ou null si le contrôle est valide.
     */
    private onlyWordAndDigit(controlName: string): ValidatorFn {
        return (formGroup: TypedFormGroup<IAttribute>): ValidationErrors | null => {
            const control = formGroup.controls[controlName];
            if (control.errors && !control.errors.onlyWordAndDigit) {
                // return if another validator has already found an error of the specified type
                return null;
            }

            let regex = new RegExp(/^[\w\d]+$/);

            // set error on Control if validation fails
            if (!regex.test(control.value as string)) {
                control.setErrors({ onlyWordAndDigit: true });
                return { onlyWordAndDigit: true };
            } else {
                control.setErrors(null);
                return null;
            }
        };
    }
}
